import {
  ArrowRight,
  BadgeCheck,
  Camera,
  ChevronRight,
  CircleDollarSign,
  ImageUpscale,
  Layers3,
  PackageCheck,
  ScanFace,
  WandSparkles
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BeforeAfter } from '../components/BeforeAfter';
import { HeroWaveField } from '../components/HeroWaveField';
import { ImageWorkspace } from '../components/ImageWorkspace';
import { FreeImageWorkspace } from '../components/FreeImageWorkspace';
import { ToolCard } from '../components/ToolCard';
import { toolMap, tools } from '../data/tools';
import heroBefore from '../assets/hero-before.png';
import heroAfter from '../assets/hero-after.png';
import { Seo } from '../lib/seo';
import { absoluteUrl } from '../lib/site';

const useCases = [
  {
    icon: Camera,
    title: 'Creators and photographers',
    description: 'Clean soft exports, low-light photos, portraits and social media images without flattening natural texture.'
  },
  {
    icon: PackageCheck,
    title: 'Online stores',
    description: 'Prepare clearer product images, transparent cutouts and consistent marketplace-ready visuals.'
  },
  {
    icon: ScanFace,
    title: 'Family archives',
    description: 'Repair scratches, fading and damaged faces while keeping the original scan available for comparison.'
  },
  {
    icon: Layers3,
    title: 'Design and campaign work',
    description: 'Upscale artwork, remove backgrounds and relight assets for covers, ads, presentations and client projects.'
  }
];

export function HomePage() {
  const location = useLocation();
  const hashTool = location.hash.startsWith('#studio-') ? location.hash.slice('#studio-'.length) : null;
  const legacyQueryTool = new URLSearchParams(location.search).get('tool');
  const requestedTool = hashTool || legacyQueryTool;
  const selectedTool = tools.find((tool) => tool.slug === requestedTool) ?? toolMap.enhance;

  useEffect(() => {
    if (!location.hash.startsWith('#studio')) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('studio')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, selectedTool.slug]);

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Nexhance AI',
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web browser',
    url: absoluteUrl('/'),
    description:
      'An online image platform with focused AI enhancement, restoration and generative editing workflows plus free browser utilities for everyday image preparation.',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '9',
      highPrice: '39',
      priceCurrency: 'USD',
      offerCount: '3'
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Nexhance AI',
    url: absoluteUrl('/'),
    description: 'Professional AI image enhancement with natural-looking results.'
  };

  return (
    <>
      <Seo
        title="AI Image Enhancer & Upscaler Online"
        description="Enhance, restore and edit images with focused AI tools, plus free browser utilities for compression, resizing, cropping, conversion, colors and metadata-clean exports."
        path="/"
        jsonLd={[websiteSchema, softwareSchema]}
      />

      <section className="hero-section">
        <HeroWaveField />
        <div className="hero-glow hero-glow-one" />
        <div className="hero-glow hero-glow-two" />
        <div className="hero-noise" aria-hidden="true" />
        <div className="shell hero-grid">
          <div className="hero-copy">
            <h1>Enhance image quality with AI—<em>without the overprocessed look.</em></h1>
            <p>Enhance and restore difficult images with focused AI workflows, then handle everyday compression, resizing, cropping, conversion and color tasks with clearly separated free browser utilities.</p>
            <div className="hero-actions">
              <Link to={`/#studio-${selectedTool.slug}`} className="button button-primary button-large">Use {selectedTool.shortName} <ArrowRight size={18} /></Link>
              <Link to="/tools" className="button button-ghost button-large">Explore all tools</Link>
            </div>
            <div className="trust-row">
              <span><BadgeCheck size={15} /> Private image processing</span>
              <span><BadgeCheck size={15} /> Before-and-after review</span>
              <span><CircleDollarSign size={15} /> Transparent credit pricing</span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-card-label"><WandSparkles size={16} /> Natural detail, not plastic texture</div>
            <BeforeAfter
              before={heroBefore}
              after={heroAfter}
              alt="Rocky coastline at sunset, shown"
              priority
              autoPreview
              className="hero-compare"
            />
            <div className="hero-chip hero-chip-one"><ImageUpscale size={14} /><span>2× / 4× / 8× upscale</span></div>
            <div className="floating-metric metric-one"><strong>25</strong><span>AI + free image tools</span></div>
          </div>
        </div>

      </section>

      <section id="studio" className="studio-section section">
        <div className="shell">
          <div className="section-heading centered">
            <p className="eyebrow">{selectedTool.eyebrow}</p>
            <h2>{selectedTool.name}: upload once, then control only what matters.</h2>
            <p>{selectedTool.description}</p>
          </div>
          {selectedTool.mode === 'free' ? <FreeImageWorkspace key={selectedTool.slug} tool={selectedTool} compact /> : <ImageWorkspace key={selectedTool.slug} tool={selectedTool} compact />}
        </div>
      </section>

      <section className="section problem-section">
        <div className="shell">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">One problem, one focused workflow</p>
              <h2>Choose the right repair instead of stacking random filters.</h2>
            </div>
            <div>
              <p>Low resolution, blur, noise, faded color and poor lighting are different problems. Nexhance AI separates them so the result is easier to control and compare.</p>
              <Link to="/guides/fix-blurry-noisy-low-resolution-photos" className="inline-link">Learn which tool to use <ChevronRight size={17} /></Link>
            </div>
          </div>
          <div className="problem-grid">
            <article><span>01</span><strong>Too small</strong><p>Upscale 2×, 4× or 8× for print, crops, marketplace zoom and larger creative exports.</p></article>
            <article><span>02</span><strong>Blurred</strong><p>Use AI deblur for camera shake, motion smear and soft focus before sharpening.</p></article>
            <article><span>03</span><strong>Noisy</strong><p>Reduce high-ISO grain and compression blocks without flattening every texture.</p></article>
            <article><span>04</span><strong>Damaged</strong><p>Repair scratches, fading and old-photo defects with separate restoration controls.</p></article>
          </div>
        </div>
      </section>

      <section className="section tools-showcase">
        <div className="shell">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">AI tools + free utilities</p>
              <h2>Specialist AI workflows and everyday free utilities stay clearly separated.</h2>
            </div>
            <div>
              <p>AI tools show a credit estimate before processing. Free utilities are labeled clearly and use 0 credits because they run directly in a supported browser.</p>
              <Link to="/tools" className="inline-link">Explore all 25 tools <ChevronRight size={17} /></Link>
            </div>
          </div>
          <div className="tool-grid">
            {tools.slice(0, 6).map((tool, index) => <ToolCard key={tool.slug} tool={tool} index={index} />)}
          </div>
        </div>
      </section>

      <section className="section how-section">
        <div className="shell how-grid">
          <div className="section-heading">
            <p className="eyebrow">How Nexhance AI works</p>
            <h2>A professional image workflow in three clear steps.</h2>
            <p>No confusing technical controls. Nexhance AI shows the decisions that matter and keeps the workflow focused.</p>
          </div>
          <div className="how-steps">
            <article><span>01</span><div><h3>Upload and validate</h3><p>The file type, dimensions and safety limits are checked before secure processing begins.</p></div></article>
            <article><span>02</span><div><h3>Apply the right workflow automatically</h3><p>The selected tool determines the correct processing path, settings and credit cost without asking users to choose technical systems.</p></div></article>
            <article><span>03</span><div><h3>Compare and download</h3><p>Use the before-and-after slider to check real improvement, then download the temporary result while it is available.</p></div></article>
          </div>
        </div>
      </section>

      <section className="section quality-section">
        <div className="shell quality-grid">
          <div className="quality-copy">
            <p className="eyebrow">Quality-first processing</p>
            <h2>Affordable where possible. Premium where it matters.</h2>
            <p>Nexhance AI uses efficient processing for routine enlargement and finishing, then reserves more intensive restoration for images where recovered detail creates real value.</p>
            <div className="quality-points">
              <div><span>01</span><div><strong>Standard enhancement</strong><p>Fast, lower-credit cleanup for everyday photos and exports.</p></div></div>
              <div><span>02</span><div><strong>Specialist restoration</strong><p>Face, scratch, blur and noise workflows for visibly damaged images.</p></div></div>
              <div><span>03</span><div><strong>Creative finishing</strong><p>Relighting and product-focused detail recovery for professional assets.</p></div></div>
            </div>
          </div>
          <div className="model-stack">
            <div className="model-card active"><span>Adjustable enhancement</span><strong>Professional detail control</strong><small>Natural recovery with user-controlled intensity</small></div>
            <div className="model-card"><span>Archival restoration</span><strong>Scratch + color recovery</strong><small>Old photographs and family scans</small></div>
            <div className="model-card"><span>Commercial cutout</span><strong>Background removal workflow</strong><small>Products, portraits and campaigns</small></div>
            <div className="model-card"><span>Creative upscaling</span><strong>Premium texture recovery</strong><small>Print, AI art and e-commerce</small></div>
          </div>
        </div>
      </section>

      <section className="section audience-section">
        <div className="shell">
          <div className="section-heading centered">
            <p className="eyebrow">Built for real image work</p>
            <h2>From one family photo to a weekly production workflow.</h2>
            <p>Use one account across restoration, content creation, product photography and client delivery.</p>
          </div>
          <div className="audience-grid">
            {useCases.map((item) => {
              const Icon = item.icon;
              return <article key={item.title}><span className="tool-icon"><Icon size={21} /></span><h3>{item.title}</h3><p>{item.description}</p></article>;
            })}
          </div>
        </div>
      </section>

      <section className="section seo-explainer-section">
        <div className="shell seo-explainer-grid">
          <div>
            <p className="eyebrow">What makes a good AI image enhancer?</p>
            <h2>Better results start with honest limits and useful comparison.</h2>
          </div>
          <div className="seo-copy-columns">
            <p>An AI photo enhancer can improve clarity, edge definition, texture and tonal balance, but it cannot recreate every fact that was absent from the original. Stronger reconstruction can estimate plausible detail, which is why professional review still matters.</p>
            <p>Nexhance AI keeps enhancement, upscaling, restoration, deblur and denoise separate. That makes it easier to solve the real defect, reduce unnecessary processing and preserve a more natural result. <Link to="/methodology" className="inline-link">See how results and guidance are reviewed.</Link></p>
          </div>
        </div>
      </section>

      <section className="section home-guide-section">
        <div className="shell">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">Image quality knowledge base</p>
              <h2>Learn the difference between enhancement, repair and enlargement.</h2>
            </div>
            <div>
              <p>Use practical guides to choose an upscale size, calculate print resolution, fix pixelated images, reduce low-light noise and prepare faster web images without guessing.</p>
              <Link to="/guides" className="inline-link">Browse all image guides <ChevronRight size={17} /></Link>
            </div>
          </div>
          <div className="home-guide-grid">
            <Link to="/guides/2x-vs-4x-image-upscaling"><span>Upscaling</span><strong>2× vs 4× vs 8×: choose the right output</strong><small>Match pixel dimensions to the real destination.</small></Link>
            <Link to="/guides/image-resolution-dpi-print-size-guide"><span>Print resolution</span><strong>Pixels, DPI and print size explained</strong><small>Calculate what a print actually needs.</small></Link>
            <Link to="/guides/remove-jpeg-artifacts-compression"><span>Compression repair</span><strong>Remove JPEG artifacts without destroying detail</strong><small>Fix blocks and ringing before enhancement or upscaling.</small></Link>
            <Link to="/guides/optimize-images-for-web-without-quality-loss"><span>Web performance</span><strong>Optimize images without visible quality loss</strong><small>Choose dimensions, format and compression carefully.</small></Link>
            <Link to="/guides/increase-image-resolution-without-losing-quality"><span>Resolution</span><strong>Increase image resolution without losing visible quality</strong><small>Choose the right source, target pixels and upscale amount.</small></Link>
            <Link to="/guides/how-to-make-a-picture-clearer"><span>Photo clarity</span><strong>Make a blurry or low-quality picture clearer</strong><small>Match enhancement, deblur, denoise or sharpening to the real defect.</small></Link>
            <Link to="/guides/professional-headshot-photo-enhancement"><span>Portraits</span><strong>Enhance a professional headshot naturally</strong><small>Improve clarity and light without changing the person.</small></Link>
            <Link to="/guides/transparent-png-background-guide"><span>Transparent PNG</span><strong>Remove a background and preserve clean edges</strong><small>Check hair, fur, glass, thin objects and shadows.</small></Link>
          </div>
        </div>
      </section>

      <section className="section pricing-teaser">
        <div className="shell pricing-teaser-card">
          <div>
            <p className="eyebrow">Built for repeat work</p>
            <h2>Start small, then scale with your workload.</h2>
            <p>AI processing is available through account credits, while the free browser utilities use 0 credits and do not require a subscription. Paid plans add recurring AI credits, while Creator and Studio also unlock 8× access.</p>
          </div>
          <div className="pricing-teaser-actions">
            <Link to="/pricing" className="button button-primary button-large">Compare plans <ArrowRight size={18} /></Link>
            <span>Plans start at $9 per month.</span>
          </div>
        </div>
      </section>
    </>
  );
}
